import React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { defineMessages, injectIntl } from 'react-intl';
import { Portal } from 'react-portal';
import { Button, Segment } from 'semantic-ui-react';
import { isEqual } from 'lodash';
import { v4 as uuid } from 'uuid';
import { toast } from 'react-toastify';

import BlocksForm from '@plone/volto/components/manage/Blocks/Block/BlocksForm';
import { getBlocks, emptyBlocksForm, Helmet, getBaseUrl, blockHasValue } from '@plone/volto/helpers';

import { getSlots, saveSlot } from 'mpf-slots-frontend/actions';
import { cleanupLastPlaceholders, slotsBlocksConfig } from 'mpf-slots-frontend/helpers';

import { InlineForm, Icon, Sidebar, Toolbar } from '@plone/volto/components';
import Toast from '@plone/volto/components/manage/Toast/Toast';
import Unauthorized from '@plone/volto/components/theme/Unauthorized/Unauthorized';
import SlotEditBlockWrapper, { setAllInheritedHidden, hasVisibleInheritedBlocks, hasHiddenInheritedBlocks } from './SlotEditBlockWrapper';
import SlotSchema from './schema';

import config from '@plone/volto/registry';

import { setSidebarTab } from '@plone/volto/actions';
import withObjectBrowser from '@plone/volto/components/manage/Sidebar/ObjectBrowser';

import saveSVG from '@plone/volto/icons/save.svg';
import clearSVG from '@plone/volto/icons/clear.svg';

import './main.less';

// RNF04: no máximo 5 componentes ativos simultaneamente na sidebar por
// página — conta o total efetivo (locais + herdados de páginas
// ancestrais), já que `slotData` chega da API já mesclada (ver
// `je.slots.backend`'s `Slots.get_data`), não só o que este editor mudou.
const MAX_SIDEBAR_BLOCKS = 5;

export function countActiveBlocks(formData) {
  return getBlocks(formData || {}).filter(([, block]) => (block ? blockHasValue(block) : false)).length;
}

/**
 * O backend já nega a escrita para quem não pode editar (`@slots` devolve
 * `edit: false`), mas nada aqui impedia a UI de edição inteira (arrastar
 * bloco, adicionar/remover, abas `Bloco`/`Ordem`/`Configurações`) de
 * renderizar para qualquer visitante, autenticado ou não — só o botão
 * Salvar de fato dependia de permissão. `slotsLoaded` evita mostrar
 * qualquer um dos dois estados (edição ou "Não autorizado") antes da busca
 * de `@slots` terminar, pra não piscar o estado errado por um instante.
 */
export function getSlotEditAccess({ slotsRequest = {}, slotData = {} }) {
  const slotsLoaded = !!slotsRequest.loaded || !!slotsRequest.error;
  const canEdit = !!slotData.edit;
  return {
    slotsLoaded,
    canEdit,
    showEditor: slotsLoaded && canEdit,
    showUnauthorized: slotsLoaded && !canEdit,
  };
}

const messages = defineMessages({
  save: {
    id: 'Save',
    defaultMessage: 'Save',
  },
  cancel: {
    id: 'Cancel',
    defaultMessage: 'Cancel',
  },
  error: {
    id: 'Error',
    defaultMessage: 'Error',
  },
  edit_slot: {
    id: 'Edit slot',
    defaultMessage: 'Edit slot',
  },
  max_blocks_reached: {
    id: 'Max sidebar blocks reached',
    defaultMessage: 'A sidebar permite no máximo {max} componentes ativos simultaneamente.',
  },
});

/**
 * Given a blocks form, adds an empty placeholder block is none exists
 */
function addPlaceholderBlock(formData) {
  const blocks = getBlocks(formData || {});

  const emptyBlocks = blocks.filter(([, block]) => (block ? !blockHasValue(block) : false)).map(([id]) => id);
  const readOnlyBlocks = blocks.filter(([, block]) => (block ? block.readOnly : false)).map(([id]) => id);

  const hasPlaceholder = !!emptyBlocks.find((b) => readOnlyBlocks.indexOf(b) === -1);

  const id = uuid();

  return hasPlaceholder
    ? formData
    : {
        ...formData,
        blocks: {
          ...(formData.blocks || {}),
          [id]: {
            '@type': config.settings.defaultBlockType,
          },
        },
        blocks_layout: {
          ...(formData.blocks_layout || []),
          items: [...(formData.blocks_layout?.items || []), id],
        },
      };
}

class EditSlot extends React.Component {
  constructor(props) {
    super(props);

    this.onCancel = this.onCancel.bind(this);
    this.onSave = this.onSave.bind(this);
    this.getInitialData = this.getInitialData.bind(this);
    this.applyFormData = this.applyFormData.bind(this);
    this.onToggleHideAll = this.onToggleHideAll.bind(this);

    const data = this.getInitialData(this.props);

    this.state = {
      isClient: false,
      selectedBlock: data.blocks_layout.items[0],
      data,
    };

    // special variable, needed to support blocks that rely on `onChangeBlock`
    // API to change the whole blocks + layout of the form
    this.blocksState = {};
  }

  getInitialData(props) {
    const { slotData = {} } = props;
    return addPlaceholderBlock(slotData);
  }

  componentDidMount() {
    this.setState({ isClient: true });
    this.props.getSlots(getBaseUrl(this.props.pathname), { full: true });
  }

  componentDidUpdate(prevProps) {
    const blocks = getBlocks(this.state.data);
    if (!blocks.length) {
      this.setState({ data: emptyBlocksForm() });
    }

    if (!isEqual(prevProps.slotData, this.props.slotData)) {
      this.setState({
        data: { ...this.state.data, ...this.getInitialData(this.props) },
      });
    }
  }

  onCancel() {
    this.props.history.push(getBaseUrl(this.props.pathname));
  }

  /**
   * Applies a new form `data`, unless it would push the number of active
   * (non-empty) blocks above MAX_SIDEBAR_BLOCKS — in that case, the change
   * is rejected and a toast warns the editor. Shrinking/reordering/editing
   * existing blocks is always allowed, even while already over the limit
   * (e.g. from inherited blocks), since it never *increases* the count.
   */
  applyFormData(newData) {
    const activeCount = countActiveBlocks(newData);
    const previousCount = countActiveBlocks(this.state.data);

    if (activeCount > MAX_SIDEBAR_BLOCKS && activeCount > previousCount) {
      toast.error(
        <Toast
          error
          title={this.props.intl.formatMessage(messages.error)}
          content={this.props.intl.formatMessage(messages.max_blocks_reached, {
            max: MAX_SIDEBAR_BLOCKS,
          })}
        />,
      );
      return false;
    }

    this.setState({ data: newData });
    return true;
  }

  /**
   * Ocultar/mostrar TODOS os componentes herdados de uma vez — o atalho de
   * "ocultar tudo" pedido na barra lateral. Reaproveita exatamente a mesma
   * transformação dos botões por item (`setAllInheritedHidden`), então o
   * formato salvo é idêntico ao de ocultar um a um. Não passa por
   * `applyFormData` de propósito: ocultar não aumenta a contagem de blocos
   * ativos (o teto de RNF04), então não há o que barrar.
   */
  onToggleHideAll(hide) {
    const newData = setAllInheritedHidden(this.state.data, hide);
    // Mantém `blocksState` em dia com a transformação — é dele que o
    // `onChangeField` do BlocksForm parte ao mesclar uma edição seguinte; se
    // ficasse com os blocos de antes, o próximo arraste/edição traria de volta
    // os que acabaram de ser ocultados.
    this.blocksState = {
      ...this.blocksState,
      blocks: newData.blocks,
      blocks_layout: newData.blocks_layout,
    };
    this.setState({ data: newData });
  }

  onSave() {
    const { saveSlot, pathname, slotId } = this.props;
    let { data } = this.state;
    const cleanData = cleanupLastPlaceholders(data);

    saveSlot(getBaseUrl(pathname), slotId, cleanData).then(() => {
      this.props.history.push(getBaseUrl(this.props.pathname));
    });
  }

  render() {
    const { pathname, content, slotData, slotsRequest } = this.props;
    const { data, selectedBlock, isClient } = this.state;
    const blocksConfig = slotsBlocksConfig(config.blocks.blocksConfig);
    const title = config.slots[this.props.slotId]?.title || this.props.slotId;

    const { showEditor, showUnauthorized } = getSlotEditAccess({ slotsRequest, slotData });

    return (
      <div id="slot-edit">
        <Helmet title={`Edit slot: ${title}`} />
        {isClient && showUnauthorized && <Unauthorized />}
        {isClient && showEditor && (
          <div className="ui container">
            <BlocksForm
              pathname={pathname}
              onChangeField={(id, value) => {
                if (['blocks', 'blocks_layout'].indexOf(id) > -1) {
                  const candidateBlocksState = { ...this.blocksState, [id]: value };
                  const newData = { ...data, ...candidateBlocksState };
                  if (this.applyFormData(newData)) {
                    this.blocksState = candidateBlocksState;
                  }
                }
              }}
              onChangeFormData={(newData) => this.applyFormData(newData)}
              onSelectBlock={(id) => this.setState({ selectedBlock: id })}
              selectedBlock={selectedBlock}
              title={this.props.intl.formatMessage(messages.edit_slot)}
              metadata={content}
              properties={data}
              blocksConfig={blocksConfig}
              isMainForm
            >
              {({ draginfo }, editBlock, blockProps) => (
                <SlotEditBlockWrapper draginfo={draginfo} blockProps={blockProps}>
                  {editBlock}
                </SlotEditBlockWrapper>
              )}
            </BlocksForm>
            <Portal node={document.getElementById('toolbar')}>
              <Toolbar
                pathname={this.props.pathname}
                hideDefaultViewButtons
                activity="edit"
                inner={
                  <>
                    <Button id="toolbar-save" className="save" aria-label={this.props.intl.formatMessage(messages.save)} onClick={this.onSave} disabled={this.props.updateRequest.loading} loading={this.props.updateRequest.loading}>
                      <Icon name={saveSVG} className="circled" size="30px" title={this.props.intl.formatMessage(messages.save)} />
                    </Button>
                    <Button className="cancel" aria-label={this.props.intl.formatMessage(messages.cancel)} onClick={this.onCancel}>
                      <Icon name={clearSVG} className="circled" size="30px" title={this.props.intl.formatMessage(messages.cancel)} />
                    </Button>
                  </>
                }
              />
            </Portal>
            <Portal node={document.getElementById('sidebar')}>
              <Sidebar documentTab={false} settingsTab={true} />
            </Portal>
            <Portal node={document.getElementById('sidebar-settings')}>
              <div>
                {/* `hide_all_inherited` NÃO é um dado persistido do slot: é um
                    espelho do estado dos blocos (marcado = nenhum herdado
                    visível). Por isso é derivado aqui para o checkbox refletir
                    a realidade, e a mudança é interceptada em `onChangeField`
                    para rodar a transformação em vez de gravar o booleano. */}
                <InlineForm
                  schema={SlotSchema(this.props)}
                  formData={{
                    ...data,
                    hide_all_inherited: !hasVisibleInheritedBlocks(data) && hasHiddenInheritedBlocks(data),
                  }}
                  onChangeField={(id, value) => {
                    if (id === 'hide_all_inherited') {
                      this.onToggleHideAll(value);
                      return;
                    }
                    this.setState({
                      data: {
                        ...data,
                        [id]: value,
                      },
                    });
                  }}
                />
                {false && (
                  <Segment>
                    <fieldset>
                      <legend>Block color legend</legend>
                      <div className="block">
                        <div className="block selected">Regular slot fill</div>
                      </div>
                    </fieldset>
                  </Segment>
                )}
              </div>
            </Portal>
          </div>
        )}
      </div>
    );
  }
}

export default compose(
  withRouter,
  injectIntl,
  withObjectBrowser,
  connect(
    (state, props) => {
      const slotId = props.match.params.id;
      return {
        slotId,
        pathname: props.location.pathname,
        content: state.content.data,
        updateRequest: state.slots?.patch || {},
        slotsRequest: state.slots?.get || {},
        slotData: {
          ...(state.slots.data?.items?.[slotId] || {}),
          '@components': {
            ...(state.content.data?.['@components'] || {}),
          },
        },
      };
    },
    {
      saveSlot,
      getSlots,
      setSidebarTab,
    },
  ),
)(EditSlot);
